import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  AddIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  CloseIcon,
  DeleteIcon,
  SearchIcon,
  SettingsIcon,
} from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Center,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Skeleton,
  SkeletonText,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  FiPlus,
  FiChevronRight,
  FiChevronDown,
  FiTrash2,
  FiArchive,
  FiFileText,
} from "react-icons/fi";

import { useApplicationsQuery } from "../../hooks/useApplicationsQuery";
import { useApplicationAccess } from "../../hooks/useApplicationAccess";
import QuotaExceededModal from "../../features/applications/components/QuotaExceededModal";
import JobDescriptionModal from "./JobDescriptionModal";
import PreferencesModal from "../../features/applications/components/PreferencesModal";
import MatchScoreCell from "./MatchScoreCell";
import ApplicationRowActions from "./ApplicationRowActions";
import QuotaBanner from "./QuotaBanner";
import {
  getStatusColor,
  getStatusCount,
  getUniqueValues,
} from "./utils/applicationUtils";
import { sortApplications, shouldUseClientSorting } from "./utils/sortingUtils";
import { useApplicationMutations } from "./hooks/useApplicationMutations";
import { useApplicationState } from "./hooks/useApplicationState";

export default function ApplicationTable() {
  // ✅ Custom Hooks - Clean separation of concerns  
  const state = useApplicationState();
  const mutations = useApplicationMutations();
  const queryClient = useQueryClient();

  // ✅ Determine if we should use client-side sorting
  // First, we need to know the total count - do a lightweight query
  const { data: countData } = useApplicationsQuery({
    page: 1,
    pageSize: 1,
    filters: state.appliedFilters,
    search: state.appliedSearch,
  });
  
  const totalRecords = countData?.pagination?.totalCount || 0;
  const useClientSort = shouldUseClientSorting(totalRecords, 10);



  // ✅ Server-side data fetching with conditional sorting
  const {
    data: queryData,
    isLoading: loading,
    isFetching,
    isPreviousData,
    error: queryError,
  } = useApplicationsQuery({
    page: state.currentPage,
    pageSize: state.itemsPerPage,
    filters: state.appliedFilters,
    sorting: useClientSort ? { sortBy: "applicationDate", sortOrder: "desc" } : {
      // Only send sorting params if using server-side sort
      sortBy: state.sortField,
      sortOrder: state.sortOrder,
    },
    search: state.appliedSearch,
  });

  const rawApplications = queryData?.data || [];
  const pagination = queryData?.pagination || {
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  };
  const appliedFiltersSummary = queryData?.appliedFilters;

  // ✅ Apply client-side sorting if needed (memoized to prevent re-sorting)
  const applications = useMemo(() => {
    if (!useClientSort) {
      return rawApplications; // Already sorted by server
    }
    // Sort client-side for small datasets
    return sortApplications(rawApplications, state.sortField, state.sortOrder);
  }, [rawApplications, state.sortField, state.sortOrder, useClientSort]);

  // UI-only state
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showJobDescriptionModal, setShowJobDescriptionModal] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const navigate = useNavigate();

  // Fetch access control information (handles premium vs free user logic)
  const {
    isPremium,
    canAnalyzeNew,
    canReconfigure,
    canAccessResumeCoverLetter,
    quota,
    showQuotaBanner,
    showUpgradePrompt,
    isLoading: isLoadingAccess,
  } = useApplicationAccess();

  // Banner dismissal state
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    const dismissed = localStorage.getItem('quotaBannerDismissed');
    const dismissedTime = localStorage.getItem('quotaBannerDismissedTime');

    if (dismissed && dismissedTime) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
      return hoursSinceDismissed < 24; // Show again after 24 hours
    }
    return false;
  });

  const handleDismissBanner = () => {
    setBannerDismissed(true);
    localStorage.setItem('quotaBannerDismissed', 'true');
    localStorage.setItem('quotaBannerDismissedTime', Date.now().toString());
  };

  // ✅ Server-side pagination - no need for client-side filtering
  // Applications are already filtered by the server
  const activeCount = pagination.totalCount || 0; // Total from server
  const archivedCount = 0; // Would need separate query for archived count

  // ✅ Memoize filter values from current page data
  const uniqueStatuses = useMemo(() => getUniqueValues(applications, "status"), [applications]);
  const uniqueLocations = useMemo(() => getUniqueValues(applications, "jobLocationCity"), [applications]);
  const uniqueRoles = useMemo(() => getUniqueValues(applications, "roleTitle"), [applications]);

  // Pagination Logic - now server-side
  const totalPages = pagination.totalPages || 1;
  const paginatedData = applications; // Already paginated by server

  // Navigation helpers
  const goResume = (id) => navigate(`/TailorResume/${id}`);
  const goCoverLetter = (id) => navigate(`/CoverLetter/${id}`);

  // Handle configure click with access control
  const handleConfigureClick = (app) => {
    const hasPermission = app.lastAnalyzedAt ? canReconfigure : canAnalyzeNew;
    if (!hasPermission) {
      setShowQuotaModal(true);
      return;
    }
    navigate(`/me/skillconfig/${app.id}`, {
      state: {
        jobDescription: app.jobDescription,
        company: app.companyName,
        roleTitle: app.roleTitle,
        location: app.jobLocationCity,
      },
    });
  };

  // Handle job description modal
  const handleViewJobDescription = (app) => {
    setSelectedApplication(app);
    setShowJobDescriptionModal(true);
  };

  const handleCloseJobDescriptionModal = () => {
    setShowJobDescriptionModal(false);
    setSelectedApplication(null);
  };

  // Handle preferences modal (Magic Tailor)
  const handleOpenPreferences = (app) => {
    setSelectedApplication(app);
    setShowPreferencesModal(true);
  };

  const handleClosePreferencesModal = () => {
    setShowPreferencesModal(false);
    setSelectedApplication(null);
  };

  // ✅ UI Helpers
  const renderSortIcon = (field) => {
    if (state.sortField === field) {
      return state.sortOrder === "asc" ? (
        <ChevronUpIcon color="orange.500" boxSize={4} ml={1} />
      ) : (
        <ChevronDownIcon color="orange.500" boxSize={4} ml={1} />
      );
    }
    // Always show faded icon for sortable columns
    return <ChevronUpIcon opacity={0.2} boxSize={4} ml={1} />;
  };

  const renderFilter = (label, values, current, onChange) => (
    <Popover placement="bottom-start">
      <PopoverTrigger>
        <IconButton
          size="xs"
          icon={<SettingsIcon />}
          variant="ghost"
          aria-label={`Filter ${label}`}
          _focus={{ boxShadow: "none" }}
        />
      </PopoverTrigger>
      <PopoverContent w="auto" p={2} borderColor="gray.200">
        <PopoverBody>
          <VStack align="stretch">
            {values.map((v) => (
              <Button
                key={v}
                size="sm"
                variant={current === v ? "solid" : "ghost"}
                onClick={() => onChange(v)}
                _focus={{ boxShadow: "none" }}
              >
                {v}
              </Button>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );

  // Mutation Handlers linked to hook
  const handleSaveRow = async (id) => {
    const changes = state.changedRows[id];
    const success = await mutations.saveApplication(id, changes);
    if (success) state.clearRowChanges(id);
  };

  const handleDelete = () => mutations.deleteApplications(applications, state.selectedIds).then(success => success && state.clearSelection());
  const handleArchive = () => mutations.archiveApplications(applications, state.selectedIds).then(success => success && state.clearSelection());
  const handleUnarchive = () => mutations.unarchiveApplications(applications, state.selectedIds).then(success => success && state.clearSelection());
  
  const handleInlineChange = (id, field, val) => {
    const originalValue = applications.find(a => a.id === id)?.[field];
    state.markRowChanged(id, field, originalValue, val);
    
    // Also update cache optimistically for local feel
    mutations.updateCache((apps) =>
      apps.map((app) => (app.id === id ? { ...app, [field]: val } : app)),
    );
  };

  // Skeleton Loading Component
  const SkeletonLoader = () => (
    <Box p={8} bg="gray.25" minH="100vh">
      {/* Header Skeleton */}
      <Flex justify="space-between" align="center" mb={10}>
        <Box>
          <Skeleton height="32px" width="200px" mb={2} borderRadius="md" />
          <Skeleton height="16px" width="150px" borderRadius="md" />
        </Box>
        <Skeleton height="40px" width="100px" borderRadius="lg" />
      </Flex>

      {/* Status Cards Skeleton */}
      <Flex gap={3} mb={10}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Box
            key={index}
            p={3}
            bg="white"
            borderRadius="xl"
            minW="130px"
            border="2px solid"
            borderColor="gray.100"
            boxShadow="0 2px 8px rgba(0, 0, 0, 0.04)"
          >
            <Skeleton height="32px" width="40px" mb={2} borderRadius="md" />
            <Skeleton height="12px" width="80px" borderRadius="md" />
          </Box>
        ))}
      </Flex>

      {/* Table Skeleton */}
      <Box
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="xl"
        overflow="hidden"
        boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
      >
        <Table variant="simple" size="md">
          <Thead bg="gray.50">
            <Tr>
              <Th
                w="50px"
                p={3}
                borderColor="gray.200"
                borderBottom="2px solid"
                borderBottomColor="gray.100"
              >
                <Skeleton height="16px" width="16px" borderRadius="sm" />
              </Th>
              <Th
                p={3}
                borderColor="gray.200"
                borderBottom="2px solid"
                borderBottomColor="gray.100"
              >
                <Skeleton height="16px" width="40px" borderRadius="md" />
              </Th>
              <Th
                p={3}
                borderColor="gray.200"
                borderBottom="2px solid"
                borderBottomColor="gray.100"
              >
                <Skeleton height="16px" width="50px" borderRadius="md" />
              </Th>
              <Th
                p={3}
                borderColor="gray.200"
                borderBottom="2px solid"
                borderBottomColor="gray.100"
              >
                <Skeleton height="16px" width="70px" borderRadius="md" />
              </Th>
              <Th
                p={3}
                borderColor="gray.200"
                borderBottom="2px solid"
                borderBottomColor="gray.100"
              >
                <Skeleton height="16px" width="60px" borderRadius="md" />
              </Th>
              <Th
                p={3}
                borderColor="gray.200"
                borderBottom="2px solid"
                borderBottomColor="gray.100"
              >
                <Skeleton height="16px" width="50px" borderRadius="md" />
              </Th>
              <Th
                p={3}
                borderColor="gray.200"
                borderBottom="2px solid"
                borderBottomColor="gray.100"
              >
                <Skeleton height="16px" width="70px" borderRadius="md" />
              </Th>
              <Th
                p={3}
                borderColor="gray.200"
                borderBottom="2px solid"
                borderBottomColor="gray.100"
              >
                <Skeleton height="16px" width="40px" borderRadius="md" />
              </Th>
              <Th
                p={3}
                borderColor="gray.200"
                borderBottom="2px solid"
                borderBottomColor="gray.100"
              >
                <Skeleton height="16px" width="90px" borderRadius="md" />
              </Th>
              <Th
                p={3}
                textAlign="right"
                borderColor="gray.200"
                borderBottom="2px solid"
                borderBottomColor="gray.100"
              >
                <Skeleton
                  height="16px"
                  width="60px"
                  borderRadius="md"
                  ml="auto"
                />
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {Array.from({ length: 8 }).map((_, index) => (
              <Tr key={index} borderBottom="1px solid" borderColor="gray.100">
                <Td p={3}>
                  <Skeleton height="16px" width="16px" borderRadius="sm" />
                </Td>
                <Td p={3}>
                  <Skeleton height="16px" width="80px" borderRadius="md" />
                </Td>
                <Td p={3}>
                  <Skeleton height="16px" width="120px" borderRadius="md" />
                </Td>
                <Td p={3}>
                  <Skeleton height="16px" width="100px" borderRadius="md" />
                </Td>
                <Td p={3}>
                  <Skeleton height="32px" width="100%" borderRadius="lg" />
                </Td>
                <Td p={3}>
                  <Skeleton height="24px" width="70px" borderRadius="full" />
                </Td>
                <Td p={3}>
                  <HStack spacing={2}>
                    <Skeleton height="24px" width="50px" borderRadius="full" />
                    <Skeleton height="20px" width="20px" borderRadius="sm" />
                  </HStack>
                </Td>
                <Td p={3}>
                  <Skeleton height="32px" width="60px" borderRadius="lg" />
                </Td>
                <Td p={3} textAlign="right">
                  <HStack spacing={2} justify="flex-end">
                    <Skeleton height="32px" width="80px" borderRadius="lg" />
                    <Skeleton height="32px" width="100px" borderRadius="lg" />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination Skeleton */}
      <Flex justify="space-between" align="center" mt={8}>
        <Skeleton height="16px" width="200px" borderRadius="md" />
        <HStack spacing={1}>
          <Skeleton height="32px" width="80px" borderRadius="lg" />
          <Skeleton height="32px" width="40px" borderRadius="lg" />
          <Skeleton height="32px" width="40px" borderRadius="lg" />
          <Skeleton height="32px" width="40px" borderRadius="lg" />
          <Skeleton height="32px" width="60px" borderRadius="lg" />
        </HStack>
      </Flex>
    </Box>
  );



  return (
    <Box p={8} bg="gray.25" minH="100vh">
      {/* Quota Exceeded Modal - Only render when needed */}
      {showQuotaModal && (
        <QuotaExceededModal
          isOpen={showQuotaModal}
          onClose={() => setShowQuotaModal(false)}
          onUpgrade={() => {
            setShowQuotaModal(false);
            navigate("/pricing");
          }}
          quota={quota}
        />
      )}

      {/* Job Description Modal */}
      <JobDescriptionModal
        isOpen={showJobDescriptionModal}
        onClose={handleCloseJobDescriptionModal}
        application={selectedApplication}
      />

      {/* Preferences Modal - Magic Tailor */}
      <PreferencesModal
        isOpen={showPreferencesModal}
        onClose={handleClosePreferencesModal}
      />

      {/* Quota Banner - Minimal & Dismissible */}
      <QuotaBanner
        quota={quota}
        show={showQuotaBanner && !bannerDismissed}
        onDismiss={handleDismissBanner}
        onUpgrade={() => navigate("/pricing")}
      />

      {/* Polished Header */}
      <Flex justify="space-between" align="center" mb={10}>
        <Flex align="flex-start" gap={4}>
          {/* Orange Vertical Accent Bar - only for title */}
          <Box
            w="4px"
            h="32px"
            bg="linear-gradient(180deg, #FF6B35 0%, #F7931E 100%)"
            borderRadius="full"
            mt="2px"
          />
          <Box>
            <Text
              fontSize="2xl"
              fontWeight="600"
              color="gray.900"
              mb={1}
              letterSpacing="-0.025em"
            >
              Applications
            </Text>
            <Flex align="baseline" gap={1}>
              <Text fontSize="xl" color="orange.500" fontWeight="700">
                {activeCount}
              </Text>
              <Text fontSize="sm" color="gray.500" fontWeight="400">
                active {activeCount === 1 ? "application" : "applications"}
              </Text>
            </Flex>
          </Box>
        </Flex>

        {/* CTA Button - Add Job */}
        <Button
          onClick={() => navigate("/me/applications")}
          leftIcon={<FiPlus size="14px" />}
          variant="outline"
          colorScheme="orange"
          size="sm"
          fontWeight="500"
        >
          Add Job
        </Button>
      </Flex>

      {/* Status Cards */}
      <Flex gap={3} mb={6}>
        {[
          { label: "Applied", key: "All", count: activeCount },
          {
            label: "Interview",
            key: "interview",
            count: getStatusCount(applications, "interview"),
          },
          { label: "Offer", key: "offer", count: getStatusCount(applications, "offer") },
          {
            label: "Rejected",
            key: "rejected",
            count: getStatusCount(applications, "rejected"),
          },
          { label: "Archived", key: "archived", count: archivedCount },
        ].map((card) => (
          <Box
            key={card.key}
            p={3}
            bg="rgba(255, 255, 255, 0.7)"
            backdropFilter="blur(12px)"
            borderRadius="xl"
            minW="130px"
            cursor="pointer"
            onClick={() => state.setStatusFilter(card.key)}
            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 8px 30px rgba(251, 146, 60, 0.15)",
              bg: "rgba(255, 255, 255, 0.9)",
            }}
            border="1px solid"
            borderColor={state.statusFilter === card.key ? "orange.300" : "rgba(255,255,255,0.6)"}
            boxShadow={
              state.statusFilter === card.key
                ? "0 4px 20px rgba(251, 146, 60, 0.2)"
                : "0 2px 10px rgba(0, 0, 0, 0.05)"
            }
            position="relative"
            overflow="hidden"
          >
            {state.statusFilter === card.key && (
              <Box
                position="absolute"
                top="-1px"
                left="-1px"
                right="-1px"
                h="3px"
                bg="linear-gradient(90deg, #FF6B35 0%, #F7931E 100%)"
                borderRadius="xl xl 0 0"
              />
            )}
            <Text
              fontSize="2xl"
              fontWeight="700"
              color="gray.900"
              mb={2}
              letterSpacing="-0.02em"
            >
              {card.count}
            </Text>
            <Text
              fontSize="xs"
              color="gray.600"
              textTransform="uppercase"
              letterSpacing="0.05em"
              fontWeight="500"
            >
              {card.label}
            </Text>
          </Box>
        ))}
      </Flex>

      {/* Search Bar - Dedicated Row */}
      <Flex justify="flex-end" align="center" mb={10} gap={2}>
        {/* Search Input */}
        <InputGroup maxW="350px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search applications..."
            value={state.searchQuery}
            onChange={(e) => state.setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                state.applyFilters();
              }
            }}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            _hover={{ borderColor: "gray.300" }}
            _focus={{
              borderColor: "orange.300",
              boxShadow: "0 0 0 1px rgba(251, 146, 60, 0.3)",
            }}
            transition="all 0.2s ease"
            fontSize="sm"
            size="sm"
          />
        </InputGroup>
        
        {/* Search Button */}
        <Button
          type="button"
          leftIcon={<SearchIcon />}
          size="sm"
          onClick={state.applyFilters}
          isLoading={isFetching && !isPreviousData}
          loadingText="Searching..."
          bg="orange.500"
          color="white"
          borderRadius="md"
          px={4}
          fontWeight="500"
          _hover={{
            bg: "orange.600",
          }}
          _active={{
            bg: "orange.700",
          }}
          transition="background 0.2s ease"
        >
          Search
        </Button>
        
        {/* Clear Filters Button */}
        {(state.statusFilter !== "All" || state.locationFilter !== "All" || state.searchQuery) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={state.clearAllFilters}
            borderRadius="lg"
            colorScheme="gray"
          >
            Clear
          </Button>
        )}
      </Flex>

      {/* Enhanced Bulk Actions */}
      {state.selectedIds.length > 0 && (
        <Box mb={8}>
          <Flex justify="flex-end" gap={3}>
            <Button
              colorScheme="red"
              leftIcon={<DeleteIcon />}
              size="sm"
              onClick={handleDelete}
              variant="outline"
              borderRadius="lg"
              _hover={{
                bg: "red.50",
                borderColor: "red.300",
                transform: "translateY(-1px)",
              }}
              transition="all 0.2s ease"
              _focus={{ boxShadow: "none" }}
            >
              Delete ({state.selectedIds.length})
            </Button>
            {state.statusFilter === "archived" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnarchive}
                borderRadius="lg"
                _hover={{
                  bg: "gray.50",
                  borderColor: "gray.300",
                  transform: "translateY(-1px)",
                }}
                transition="all 0.2s ease"
                _focus={{ boxShadow: "none" }}
              >
                Unarchive ({state.selectedIds.length})
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleArchive}
                borderRadius="lg"
                _hover={{
                  bg: "gray.50",
                  borderColor: "gray.300",
                  transform: "translateY(-1px)",
                }}
                transition="all 0.2s ease"
                _focus={{ boxShadow: "none" }}
              >
                Archive ({state.selectedIds.length})
              </Button>
            )}
          </Flex>
        </Box>
      )}

      {/* Clean Table */}
      <>
        <Box
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="xl"
          overflow="hidden"
          boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
        >
          {/* Horizontal scroll wrapper for all screen sizes */}
          <Box overflowX="auto">
          <Table variant="simple" size={{ base: "sm", md: "md" }} minW="900px">
            <Thead bg="gray.50">
              <Tr>
                <Th
                  w="50px"
                  p={3}
                  borderColor="gray.200"
                  borderBottom="2px solid"
                  borderBottomColor="gray.100"
                ></Th>
                <Th
                  p={3}
                  cursor="pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    state.toggleSort("applicationDate");
                  }}
                  borderColor="gray.200"
                  borderBottom="2px solid"
                  borderBottomColor="gray.100"
                  _hover={{ bg: "orange.50" }}
                  transition="all 0.15s ease"
                  userSelect="none"
                >
                  <Flex align="center" gap={1}>
                    <Text fontWeight="600" fontSize="sm" color="gray.700">
                      Date
                    </Text>
                    {renderSortIcon("applicationDate")}
                  </Flex>
                </Th>
                <Th
                  p={3}
                  cursor="pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    state.toggleSort("roleTitle");
                  }}
                  borderColor="gray.200"
                  borderBottom="2px solid"
                  borderBottomColor="gray.100"
                  _hover={{ bg: "orange.50" }}
                  transition="all 0.15s ease"
                  userSelect="none"
                >
                  <Flex align="center" gap={1}>
                    <Text fontWeight="600" fontSize="sm" color="gray.700">
                      Role
                    </Text>
                    {renderSortIcon("roleTitle")}
                  </Flex>
                </Th>
                <Th
                  p={3}
                  cursor="pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    state.toggleSort("companyName");
                  }}
                  borderColor="gray.200"
                  borderBottom="2px solid"
                  borderBottomColor="gray.100"
                  _hover={{ bg: "orange.50" }}
                  transition="all 0.15s ease"
                  userSelect="none"
                >
                  <Flex align="center" gap={1}>
                    <Text fontWeight="600" fontSize="sm" color="gray.700">
                      Company
                    </Text>
                    {renderSortIcon("companyName")}
                  </Flex>
                </Th>
                <Th
                  p={3}
                  cursor="pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    state.toggleSort("jobLocationCity");
                  }}
                  borderColor="gray.200"
                  borderBottom="2px solid"
                  borderBottomColor="gray.100"
                  _hover={{ bg: "orange.50" }}
                  transition="all 0.15s ease"
                  userSelect="none"

                >
                  <Flex align="center" gap={1}>
                    <Text fontWeight="600" fontSize="sm" color="gray.700">
                      Location
                    </Text>
                    {renderSortIcon("jobLocationCity")}
                    {renderFilter(
                      "Location",
                      uniqueLocations,
                      state.locationFilter,
                      state.setLocationFilter,
                    )}
                  </Flex>
                </Th>
                <Th
                  p={3}
                  cursor="pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    state.toggleSort("status");
                  }}
                  borderColor="gray.200"
                  borderBottom="2px solid"
                  borderBottomColor="gray.100"
                  _hover={{ bg: "orange.50" }}
                  transition="all 0.15s ease"
                  userSelect="none"
                >
                  <Flex align="center" gap={1}>
                    <Text fontWeight="600" fontSize="sm" color="gray.700">
                      Status
                    </Text>
                    {renderSortIcon("status")}
                    {renderFilter(
                      "Status",
                      uniqueStatuses,
                      state.statusFilter,
                      state.setStatusFilter,
                    )}
                  </Flex>
                </Th>
                <Th
                  p={3}
                  borderColor="gray.200"
                  borderBottom="2px solid"
                  borderBottomColor="gray.100"

                >
                  <Text fontWeight="600" fontSize="sm" color="gray.700">
                    Match Score
                  </Text>
                </Th>
                <Th
                  p={3}
                  borderColor="gray.200"
                  borderBottom="2px solid"
                  borderBottomColor="gray.100"

                >
                  <Text fontWeight="600" fontSize="sm" color="gray.700">
                    Notes
                  </Text>
                </Th>
                <Th
                  p={3}
                  borderColor="gray.200"
                  borderBottom="2px solid"
                  borderBottomColor="gray.100"

                >
                  <Text fontWeight="600" fontSize="sm" color="gray.700">
                    Job Description
                  </Text>
                </Th>
                <Th
                  p={3}
                  textAlign="right"
                  borderColor="gray.200"
                  borderBottom="2px solid"
                  borderBottomColor="gray.100"
                >
                  <Text fontWeight="600" fontSize="sm" color="gray.700">
                    Actions
                  </Text>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, index) => (
                  <Tr key={index} borderBottom="1px solid" borderColor="gray.100">
                    <Td p={3}>
                      <Skeleton height="16px" width="16px" borderRadius="sm" />
                    </Td>
                    <Td p={3}>
                      <Skeleton height="16px" width="80px" borderRadius="md" />
                    </Td>
                    <Td p={3}>
                      <Skeleton height="16px" width="120px" borderRadius="md" />
                    </Td>
                    <Td p={3}>
                      <Skeleton height="16px" width="100px" borderRadius="md" />
                    </Td>
                    <Td p={3}>
                      <Skeleton height="32px" width="100%" borderRadius="lg" />
                    </Td>
                    <Td p={3}>
                      <Skeleton height="24px" width="70px" borderRadius="full" />
                    </Td>
                    <Td p={3}>
                      <HStack spacing={2}>
                        <Skeleton height="24px" width="50px" borderRadius="full" />
                        <Skeleton height="20px" width="20px" borderRadius="sm" />
                      </HStack>
                    </Td>
                    <Td p={3}>
                      <Skeleton height="32px" width="60px" borderRadius="lg" />
                    </Td>
                    <Td p={3}>
                      <Skeleton height="32px" width="80px" borderRadius="lg" />
                    </Td>
                    <Td p={3} textAlign="right">
                      <HStack spacing={2} justify="flex-end">
                        <Skeleton height="32px" width="80px" borderRadius="lg" />
                        <Skeleton height="32px" width="100px" borderRadius="lg" />
                      </HStack>
                    </Td>
                  </Tr>
                ))
              ) : (
                paginatedData.map((app) => {
                const isDisabled = !!app.bArchive;
                const hasChanges = !!state.changedRows[app.id];
                const rowIsSaving = !!mutations.isSaving[app.id];
                const isExpanded = !!state.expandedRows[app.id];

                return (
                  <React.Fragment key={app.id}>
                    <Tr
                      _hover={{ 
                        bg: isDisabled ? "gray.50" : hasChanges ? "orange.50" : "orange.25",
                      }}
                      bg={hasChanges ? "orange.25" : "white"}
                      borderBottom={isExpanded ? "none" : "1px solid"}
                      borderColor="gray.100"
                      transition="all 0.15s ease-in-out"
                    >
                      <Td p={3}>
                        <Checkbox
                          isChecked={state.selectedIds.includes(app.id)}
                          onChange={() => state.toggleSelect(app.id)}
                          colorScheme="orange"
                          size="md"
                        />
                      </Td>
                      <Td p={3}>
                        <Text fontSize="sm" color="gray.600" fontWeight="500">
                          {new Date(app.applicationDate).toLocaleDateString()}
                        </Text>
                      </Td>
                      <Td p={3}>
                        <Text fontWeight="600" color="gray.900" fontSize="sm">
                          {app.roleTitle}
                        </Text>
                      </Td>
                      <Td p={3}>
                        <Text color="gray.700" fontSize="sm" fontWeight="500">
                          {app.companyName}
                        </Text>
                      </Td>
                      <Td p={3}>
                        <Input
                          size="sm"
                          value={app.jobLocationCity || ""}
                          onChange={(e) =>
                            handleInlineChange(
                              app.id,
                              "jobLocationCity",
                              e.target.value,
                            )
                          }
                          borderRadius="lg"
                          bg="gray.50"
                          border="1px solid"
                          borderColor="gray.200"
                          _hover={{ borderColor: "gray.300" }}
                          transition="all 0.2s ease"
                        />
                      </Td>
                      <Td p={3}>
                        <Badge
                          colorScheme={getStatusColor(app.status)}
                          variant="subtle"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                          textTransform="capitalize"
                          fontWeight="600"
                        >
                          {app.status || "Applied"}
                        </Badge>
                      </Td>
                      <Td p={3}>
                        <MatchScoreCell
                          application={app}
                          canAnalyzeNew={canAnalyzeNew}
                          canReconfigure={canReconfigure}
                          isPremium={isPremium}
                          onConfigureClick={handleConfigureClick}
                          onUpgradeClick={() => setShowQuotaModal(true)}
                          onOpenPreferences={handleOpenPreferences}
                          quotaInfo={quota}
                        />
                      </Td>
                      <Td p={3}>
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={
                            isExpanded ? <FiChevronDown /> : <FiChevronRight />
                          }
                          onClick={() => state.toggleRowExpansion(app.id)}
                          color="gray.600"
                          _hover={{
                            color: "gray.800",
                            bg: "gray.100",
                          }}
                          fontSize="sm"
                          borderRadius="lg"
                          transition="all 0.2s ease"
                          _focus={{ boxShadow: "none" }}
                        >
                          {app.notes ? "Notes" : "Add"}
                        </Button>
                      </Td>
                      <Td p={3}>
                        <Button
                          size="sm"
                          variant="solid"
                          bg="purple.50"
                          color="purple.600"
                          border="1px solid"
                          borderColor="purple.200"
                          leftIcon={<FiFileText />}
                          fontSize="sm"
                          fontWeight="500"
                          borderRadius="lg"
                          px={4}
                          py={2}
                          _hover={{
                            bg: "purple.100",
                            borderColor: "purple.300",
                            transform: "translateY(-1px)",
                          }}
                          _active={{
                            bg: "purple.200",
                          }}
                          _focus={{ boxShadow: "0 0 0 3px rgba(139, 92, 246, 0.1)" }}
                          transition="all 0.2s ease"
                          onClick={() => handleViewJobDescription(app)}
                        >
                          View
                        </Button>
                      </Td>
                      <Td p={3} textAlign="right">
                        <Flex justify="flex-end" align="center" gap={2}>
                          <ApplicationRowActions
                            application={app}
                            hasChanges={hasChanges}
                            isSaving={rowIsSaving}
                            isDisabled={isDisabled}
                            canAccessResumeCoverLetter={canAccessResumeCoverLetter}
                            onSave={() => handleSaveRow(app.id)}
                            onCancel={() => state.clearRowChanges(app.id)}
                            onResumeClick={() => goResume(app.id)}
                            onCoverLetterClick={() => goCoverLetter(app.id)}
                          />
                        </Flex>
                      </Td>
                    </Tr>

                    {/* Expandable Notes Row */}
                    <Tr>
                      <Td
                        colSpan={10}
                        p={0}
                        borderBottom="1px solid"
                        borderColor="gray.100"
                      >
                        <Collapse in={isExpanded}>
                          <Box
                            p={8}
                            bg="gray.25"
                            borderTop="1px solid"
                            borderColor="gray.100"
                          >
                            <VStack align="stretch" spacing={5}>
                              <HStack justify="space-between" align="center">
                                <Text
                                  fontWeight="600"
                                  color="gray.800"
                                  fontSize="sm"
                                >
                                  Notes & Status
                                </Text>
                                <Select
                                  size="sm"
                                  value={app.status || ""}
                                  onChange={(e) =>
                                    handleInlineChange(
                                      app.id,
                                      "status",
                                      e.target.value,
                                    )
                                  }
                                  maxW="160px"
                                  borderRadius="lg"
                                  bg="white"
                                  border="1px solid"
                                  borderColor="gray.200"
                                  _hover={{ borderColor: "gray.300" }}
                                  transition="all 0.2s ease"
                                >
                                  <option value="applied">Applied</option>
                                  <option value="interview">Interview</option>
                                  <option value="offer">Offer</option>
                                  <option value="rejected">Rejected</option>
                                </Select>
                              </HStack>
                              <Textarea
                                value={app.notes || ""}
                                onChange={(e) =>
                                  handleInlineChange(app.id, "notes", e.target.value)
                                }
                                placeholder="Add notes about this application..."
                                rows={4}
                                bg="white"
                                borderRadius="lg"
                                border="1px solid"
                                borderColor="gray.200"
                                _hover={{ borderColor: "gray.300" }}
                                transition="all 0.2s ease"
                                resize="none"
                                overflowY="auto"
                              />
                            </VStack>
                          </Box>
                        </Collapse>
                      </Td>
                    </Tr>
                  </React.Fragment>
                );
               })
              )}
            </Tbody>
          </Table>
          </Box>{/* Close horizontal scroll wrapper */}

          {
            paginatedData.length === 0 && (
              <Box p={12} textAlign="center">
                <VStack spacing={4}>
                  {(() => {
                    // Check if there are no applications at all
                    const hasNoApplications = applications.length === 0;
                    
                    // Check if there are applications but search/filters returned no results
                    const hasApplicationsButFiltered = applications.length > 0 && paginatedData.length === 0;

                    const getEmptyStateContent = () => {
                      // If no applications exist at all, show "Add Job" button
                      if (hasNoApplications) {
                        return {
                          title: "Ready to start your job search?",
                          message: "Add your first application and begin tracking your journey to success.",
                          showButton: true,
                        };
                      }

                      // If applications exist but search/filters returned no results
                      if (hasApplicationsButFiltered) {
                        if (state.searchQuery) {
                          return {
                            title: "No applications found",
                            message: `No applications match "${state.searchQuery}". Try adjusting your search or filters.`,
                            showButton: false,
                          };
                        }

                        switch (state.statusFilter) {
                          case "interview":
                            return {
                              title: "No interviews yet",
                              message: "Keep applying! Interviews will start coming in as you build momentum.",
                              showButton: false,
                            };
                          case "offer":
                            return {
                              title: "No offers yet",
                              message: "Stay positive! Each application gets you closer to that perfect offer.",
                              showButton: false,
                            };
                          case "rejected":
                            return {
                              title: "No rejections yet",
                              message: "That's great! Keep up the momentum with your applications.",
                              showButton: false,
                            };
                          case "archived":
                            return {
                              title: "No archived applications",
                              message: "Your active applications are all visible in the main view.",
                              showButton: false,
                            };
                          default:
                            return {
                              title: "No applications found",
                              message: "Try adjusting your filters to see more results.",
                              showButton: false,
                            };
                        }
                      }

                      // Default fallback
                      return {
                        title: "No applications found",
                        message: "Try adjusting your search or filters.",
                        showButton: false,
                      };
                    };

                    const content = getEmptyStateContent();

                    return (
                      <>
                        <Text fontSize="md" color="gray.600" fontWeight="500">
                          {content.title}
                        </Text>
                        <Text fontSize="sm" color="gray.500" maxW="400px">
                          {content.message}
                        </Text>
                        {content.showButton && (
                          <Button
                            bg="orange.500"
                            color="white"
                            leftIcon={<FiPlus />}
                            onClick={() => navigate("/me/applications")}
                            _hover={{ bg: "orange.600" }}
                            size="sm"
                            mt={2}
                          >
                            Add Your First Job
                          </Button>
                        )}
                      </>
                    );
                  })()}
                </VStack>
              </Box>
            )
          }
        </Box >

        {/* Enhanced Pagination */}
        {
          totalPages > 1 && (
            <Flex justify="space-between" align="center" mt={8}>
              <Flex align="center" gap={3}>
                <Text fontSize="sm" color="gray.600" fontWeight="500">
                  Showing {pagination.firstItemIndex || 1}-
                  {pagination.lastItemIndex || applications.length} of{" "}
                  {pagination.totalCount} applications
                </Text>
                {isFetching && isPreviousData && (
                  <Spinner size="sm" color="orange.500" />
                )}
              </Flex>

              <HStack spacing={1}>
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<ChevronLeftIcon />}
                  onClick={() => state.goToPage(state.currentPage - 1)}
                  isDisabled={state.currentPage === 1}
                  borderRadius="lg"
                  _hover={{
                    bg: "gray.50",
                    transform: "translateY(-1px)",
                  }}
                  transition="all 0.2s ease"
                >
                  Previous
                </Button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (page) =>
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - state.currentPage) <= 2,
                  )
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <Text color="gray.400" px={2}>
                          ...
                        </Text>
                      )}
                      <Button
                        size="sm"
                        variant={state.currentPage === page ? "solid" : "outline"}
                        colorScheme={state.currentPage === page ? "orange" : "gray"}
                        onClick={() => state.goToPage(page)}
                        borderRadius="lg"
                        minW="40px"
                        _hover={{
                          transform:
                            state.currentPage !== page ? "translateY(-1px)" : "none",
                          bg: state.currentPage !== page ? "gray.50" : undefined,
                        }}
                        transition="all 0.2s ease"
                        boxShadow={
                          state.currentPage === page
                            ? "0 2px 8px rgba(251, 146, 60, 0.3)"
                            : "none"
                        }
                      >
                        {page}
                      </Button>
                    </React.Fragment>
                  ))}

                <Button
                  size="sm"
                  variant="outline"
                  rightIcon={<ChevronRightIcon />}
                  onClick={() => state.goToPage(state.currentPage + 1)}
                  isDisabled={state.currentPage === totalPages}
                  borderRadius="lg"
                  _hover={{
                    bg: "gray.50",
                    transform: "translateY(-1px)",
                  }}
                  transition="all 0.2s ease"
                >
                  Next
                </Button>
              </HStack>
            </Flex>
          )
        }
      </>
    </Box >
  );
}
